import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Building2, Users, Target, Plus } from 'lucide-react';

const WorkspacesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🏢 Loading workspaces...');
      const workspacesRes = await api.get('/workspaces');
      console.log('🏢 Raw response:', workspacesRes);
      
      // Handle both response formats
      const workspacesData = Array.isArray(workspacesRes) ? workspacesRes : (workspacesRes.data || []);
      
      console.log('🏢 Workspaces data:', workspacesData);
      
      if (!Array.isArray(workspacesData)) {
        console.error('❌ Response is not an array:', workspacesData);
        setWorkspaces([]);
        return;
      }
      
      if (workspacesData.length === 0) {
        console.log('⚠️ No workspaces found');
        setWorkspaces([]);
        return;
      }
      
      // Load counts for each workspace
      const workspacesWithCounts = await Promise.all(
        workspacesData.map(async (workspace) => {
          try {
            const [usersRes, objRes] = await Promise.all([
              api.get(`/workspaces/${workspace.id}/users`),
              api.get(`/workspaces/${workspace.id}/objectives`)
            ]);
            
            const usersData = Array.isArray(usersRes) ? usersRes : (usersRes.data || []);
            const objData = Array.isArray(objRes) ? objRes : (objRes.data || []);
            
            return {
              ...workspace,
              usersCount: usersData.length,
              objectivesCount: objData.length
            };
          } catch (err) {
            console.error(`❌ Error loading counts for workspace ${workspace.id}:`, err);
            return {
              ...workspace,
              usersCount: 0,
              objectivesCount: 0
            };
          }
        })
      );
      
      console.log('✅ Final workspaces:', workspacesWithCounts);
      setWorkspaces(workspacesWithCounts);
      
    } catch (err) {
      console.error('❌ Load workspaces error:', err);
      console.error('❌ Error response:', err.response);
      setError(err.error || err.response?.data?.error || 'Error al cargar workspaces');
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Cargando workspaces...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Workspaces</h1>
        {user?.role === 'master_admin' && (
          <button
            onClick={() => navigate('/workspaces/nuevo')}
            className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5" />
            <span>Nuevo Workspace</span>
          </button>
        )}
      </div>

      {/* Workspaces Grid */}
      {workspaces.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workspaces.map(workspace => (
            <div key={workspace.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Building2 className="w-8 h-8 text-indigo-600" />
                  <div>
                    <h3 className="text-lg font-semibold">{workspace.name}</h3>
                    <p className="text-sm text-gray-500">{workspace.slug}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{workspace.usersCount || 0} usuarios</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Target className="w-4 h-4" />
                  <span className="text-sm">{workspace.objectivesCount || 0} objetivos</span>
                </div>
              </div>

              <Link
                to={`/workspaces/${workspace.id}/usuarios`}
                className="block w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors text-center"
              >
                Ver Usuarios
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No hay workspaces disponibles</p>
          {user?.role === 'master_admin' && (
            <button
              onClick={() => navigate('/workspaces/nuevo')}
              className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
            >
              Crear Primer Workspace
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkspacesPage;
